export interface SkillMetricCard {
  label: string
  value: string
  description: string
  delta: number
  direction: 'up' | 'down'
}

interface SkillBaseline {
  attempted: number
  passed: number
  attempts: number
  finalScore: number
  firstScore: number
  mastery: number
  confidentErrors: number
  /** Percentage change vs. the previous period, in card order */
  deltas: [number, number, number, number, number, number, number, number]
}

const SKILL_BASELINES: Record<string, SkillBaseline> = {
  'All Skills Combined': {
    attempted: 86,
    passed: 85,
    attempts: 3.78,
    finalScore: 92.25,
    firstScore: 64.93,
    mastery: 65.52,
    confidentErrors: 18.18,
    deltas: [12, 14, 3, -8, 5, -3, 7, -4],
  },
  'HTML Basics': {
    attempted: 24,
    passed: 24,
    attempts: 2.4,
    finalScore: 95.1,
    firstScore: 78.4,
    mastery: 82.0,
    confidentErrors: 5.0,
    deltas: [9, 11, 2, -14, 3, 6, 9, -11],
  },
  'CSS Fundamentals': {
    attempted: 22,
    passed: 19,
    attempts: 4.6,
    finalScore: 88.2,
    firstScore: 57.6,
    mastery: 58.1,
    confidentErrors: 22.0,
    deltas: [15, 8, -5, 12, -2, -7, -4, 9],
  },
  'JavaScript Basics': {
    attempted: 21,
    passed: 19,
    attempts: 4.9,
    finalScore: 87.4,
    firstScore: 55.2,
    mastery: 54.3,
    confidentErrors: 28.0,
    deltas: [18, 10, -3, 16, -4, -9, -6, 13],
  },
  'Responsive Design': {
    attempted: 19,
    passed: 18,
    attempts: 3.2,
    finalScore: 91.0,
    firstScore: 68.7,
    mastery: 71.4,
    confidentErrors: 20.0,
    deltas: [7, 9, 4, -6, 4, 2, 5, -3],
  },
}

const RANGE_FACTORS: Record<number, { volume: number; scoreShift: number; deltaScale: number }> = {
  30: { volume: 1, scoreShift: 0, deltaScale: 1 },
  60: { volume: 1.8, scoreShift: -1.6, deltaScale: 1.3 },
  90: { volume: 2.5, scoreShift: -2.8, deltaScale: 1.6 },
}

const clampPercent = (value: number) => Math.min(99.9, Math.max(0.5, value))

/**
 * Builds the eight Skill Performance cards for the selected skill and time
 * range so the numbers move as the filters change.
 */
export function getSkillPerformanceMetrics(
  skill: string,
  days: number,
): SkillMetricCard[] {
  const base = SKILL_BASELINES[skill] || SKILL_BASELINES['All Skills Combined']
  const range = RANGE_FACTORS[days] || RANGE_FACTORS[30]

  const attempted = Math.round(base.attempted * range.volume)
  const passed = Math.min(attempted, Math.round(base.passed * range.volume))
  const completion = attempted > 0 ? (passed / attempted) * 100 : 0
  const attemptsPerLearner = base.attempts + (range.volume - 1) * 0.28
  const finalScore = clampPercent(base.finalScore + range.scoreShift)
  const firstScore = clampPercent(base.firstScore + range.scoreShift)
  const mastery = clampPercent(base.mastery + range.scoreShift)
  const confidentErrors = clampPercent(
    base.confidentErrors - range.scoreShift * 0.6,
  )

  const scaleDelta = (delta: number) =>
    Math.round(delta * range.deltaScale * 10) / 10

  const values: { label: string; value: string; description: string }[] = [
    {
      label: 'Learners Attempted',
      value: `${attempted}`,
      description:
        'Total number of learners who attempted the assessment for skill',
    },
    {
      label: 'Learners Passed',
      value: `${passed}`,
      description: 'Number of learners who successfully passed the skill',
    },
    {
      label: 'Completion Rate',
      value: `${completion.toFixed(2)}%`,
      description: 'Percentage of learners who passed the assessment',
    },
    {
      label: 'Avg. Number of Attempts',
      value: attemptsPerLearner.toFixed(2),
      description:
        'Average number of attempts per learner to complete the skill',
    },
    {
      label: 'Avg. Final Score',
      value: `${finalScore.toFixed(2)}%`,
      description: 'Average final score achieved by all learners',
    },
    {
      label: 'Avg. First Score',
      value: `${firstScore.toFixed(2)}%`,
      description: 'Average score achieved on first attempt',
    },
    {
      label: 'Avg. Mastery Rate',
      value: `${mastery.toFixed(2)}%`,
      description: 'Average mastery rate across all attempts',
    },
    {
      label: 'Avg. Confident Error Rate',
      value: `${confidentErrors.toFixed(2)}%`,
      description: 'Average confident error rate across all attempts',
    },
  ]

  return values.map((metric, index) => {
    const delta = scaleDelta(base.deltas[index])
    return {
      ...metric,
      delta: Math.abs(delta),
      direction: delta >= 0 ? 'up' : 'down',
    }
  })
}
